import { normalizeAirportTransfer } from './airportTransfer';

const STORAGE_KEY = 'amr-booking-selection-v1';

export function saveBookingSelection(selection) {
  const normalizedSelection = normalizeBookingSelection(selection);

  if (!normalizedSelection) {
    throw new Error('The selected booking offer is incomplete.');
  }

  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedSelection));
  return normalizedSelection;
}

export function readBookingSelection() {
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    if (!value) return null;
    return normalizeBookingSelection(JSON.parse(value));
  } catch {
    return null;
  }
}

export function clearBookingSelection() {
  window.sessionStorage.removeItem(STORAGE_KEY);
}

function normalizeBookingSelection(selection) {
  if (!selection || typeof selection !== 'object') return null;

  const requiredStrings = ['residenceId', 'quoteId', 'ratePlanId', 'checkInDateLocalized', 'checkOutDateLocalized', 'currency'];
  if (requiredStrings.some((key) => typeof selection[key] !== 'string' || !selection[key].trim())) return null;

  const total = Number(selection.total);
  const nights = Number(selection.nights);
  const adultsCount = Number(selection.adultsCount);
  const childrenCount = Number(selection.childrenCount);
  const guestsCount = Number(selection.guestsCount);
  const childrenAges = Array.isArray(selection.childrenAges) ? selection.childrenAges.map(Number) : [];
  const ratePlans = normalizeRatePlans(selection.ratePlans, selection);
  const bookingOptions = normalizeBookingOptions(selection.bookingOptions, childrenCount);
  const airportTransfer = normalizeAirportTransfer(selection.airportTransfer);

  if (
    !Number.isFinite(total)
    || total < 0
    || !Number.isInteger(nights)
    || nights < 1
    || !Number.isInteger(adultsCount)
    || adultsCount < 1
    || !Number.isInteger(childrenCount)
    || childrenCount < 0
    || !Number.isInteger(guestsCount)
    || guestsCount !== adultsCount + childrenCount
    || childrenAges.length !== childrenCount
    || childrenAges.some((age) => !Number.isInteger(age) || age < 0 || age > 17)
  ) {
    return null;
  }

  return {
    residenceId: selection.residenceId.trim(),
    quoteId: selection.quoteId.trim(),
    ratePlanId: selection.ratePlanId.trim(),
    ratePlanName: typeof selection.ratePlanName === 'string' ? selection.ratePlanName.trim() : '',
    isNonRefundable: selection.isNonRefundable === true,
    cancellationPolicy: Array.isArray(selection.cancellationPolicy)
      ? selection.cancellationPolicy.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim())
      : [],
    ratePlans,
    currency: selection.currency.trim(),
    total,
    nights,
    checkInDateLocalized: selection.checkInDateLocalized.trim(),
    checkOutDateLocalized: selection.checkOutDateLocalized.trim(),
    adultsCount,
    childrenCount,
    childrenAges,
    guestsCount,
    bookingOptions,
    airportTransfer,
    expiresAt: typeof selection.expiresAt === 'string' ? selection.expiresAt : '',
  };
}

function normalizeBookingOptions(value, childrenCount) {
  const hasChildren = childrenCount > 0;

  return {
    privateParking: value?.privateParking === true,
    babyCot: hasChildren && value?.babyCot === true,
    highChair: hasChildren && value?.highChair === true,
  };
}

function normalizeRatePlans(ratePlans, selection) {
  const plans = Array.isArray(ratePlans) ? ratePlans : [];
  const normalizedPlans = plans.map((ratePlan) => {
    if (!ratePlan || typeof ratePlan !== 'object') return null;

    const ratePlanId = typeof ratePlan.ratePlanId === 'string' ? ratePlan.ratePlanId.trim() : '';
    const currency = typeof ratePlan.currency === 'string' ? ratePlan.currency.trim() : '';
    const total = Number(ratePlan.total);

    if (!ratePlanId || !currency || !Number.isFinite(total) || total < 0) return null;

    return {
      ratePlanId,
      ratePlanName: typeof ratePlan.ratePlanName === 'string' ? ratePlan.ratePlanName.trim() : '',
      isNonRefundable: ratePlan.isNonRefundable === true,
      cancellationPolicy: Array.isArray(ratePlan.cancellationPolicy)
        ? ratePlan.cancellationPolicy.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim())
        : [],
      cancellationFee: typeof ratePlan.cancellationFee === 'string' ? ratePlan.cancellationFee : null,
      currency,
      total,
    };
  }).filter(Boolean);

  if (!normalizedPlans.some((ratePlan) => ratePlan.ratePlanId === selection.ratePlanId)) {
    normalizedPlans.unshift({
      ratePlanId: selection.ratePlanId.trim(),
      ratePlanName: typeof selection.ratePlanName === 'string' ? selection.ratePlanName.trim() : '',
      isNonRefundable: selection.isNonRefundable === true,
      cancellationPolicy: Array.isArray(selection.cancellationPolicy)
        ? selection.cancellationPolicy.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim())
        : [],
      cancellationFee: null,
      currency: selection.currency.trim(),
      total: Number(selection.total),
    });
  }

  return normalizedPlans;
}

export { STORAGE_KEY as BOOKING_SELECTION_STORAGE_KEY };
